import isNumber from 'lodash-es/isNumber';
import { memoizedOverscanIndicesGetterFactory } from 'mk2/helpers/reactVirtualized';
import React from 'react';
import {
    CellMeasurerCache,
    Index,
    IndexRange,
    InfiniteLoader,
    List,
    ListRowRenderer,
    WindowScroller,
} from 'react-virtualized';

interface OwnProps {
    rowHeight: number | ((params: Index) => number);
    isRowLoaded: (params: Index) => boolean;
    loadMoreRows: (params: IndexRange) => Promise<any>;
    rowCount?: number;
    rowRenderer: ListRowRenderer;

    numberOfItemsOnFirstPage?: number;

    // vyska riadku (napr. maximalna)
    // ak rowHeight nie je cislo, treba aby ReactVirtualized__Grid__innerScrollContainer
    // mal dostatocne velku height pri prvom render(). Ta sa pocita na zaklade rowCount
    // a estimatedRowHeight
    //
    // Dostatocne velku height treba napr. aby sa dala obnovit predosla scrollTop pozicia
    estimatedRowHeight?: number;

    deferredMeasurementCache?: CellMeasurerCache;

    listRef?(ref: List);
}

type Props = OwnProps;

/*
    InfiniteList funguje tak, ze ak mame 1000 itemov, kazdy vysoky 50px, tak <List>
    z 'react-virtualized' v stranke vytvori <div> element vysoky 50000px

    render() v <List>-e je ale tak chytry, ze vykresli len tie items ktore su aktualne
    viditelne na obrazovke (t.j. nevykresluje take, co su mimo obrazovky)
 */
export const InfiniteList: React.StatelessComponent<Props> = ({
    rowCount, rowRenderer, isRowLoaded, loadMoreRows, rowHeight, listRef, estimatedRowHeight,
    numberOfItemsOnFirstPage = 25, deferredMeasurementCache,
}) => {

    if (!estimatedRowHeight && !isNumber(rowHeight)) {
        throw new Error('rowHeight or estimatedRowHeight must be given as number');
    }

    return (
        <WindowScroller>
            {({ height, scrollTop, onChildScroll }) => (
                <InfiniteLoader
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    rowCount={rowCount}
                >
                    {({ onRowsRendered, registerChild }) => (
                        <List
                            autoHeight
                            autoWidth
                            ref={(ref) => {
                                if (listRef) {
                                    listRef(ref);
                                }
                                registerChild(ref);
                            }}
                            onRowsRendered={onRowsRendered}
                            rowRenderer={rowRenderer}
                            rowCount={rowCount}
                            height={height || 1/* NOTE: We need this to be non-zero, so that react-virtualized renders it's content on server */}
                            rowHeight={rowHeight}
                            estimatedRowSize={estimatedRowHeight}
                            width={1/* NOTE: We need this to be non-zero, so that react-virtualized renders it's content on server */}
                            scrollTop={scrollTop}
                            onScroll={onChildScroll}
                            overscanRowCount={5}
                            overscanIndicesGetter={memoizedOverscanIndicesGetterFactory(numberOfItemsOnFirstPage)}
                            style={process.env.SERVER && {
                                overflowX: 'auto',
                                overflowY: 'auto',
                            } /* NOTE: Hack to get around server vs client checksum */}
                            containerStyle={{
                                ...(process.env.SERVER && !rowHeight ? {
                                    height: 'auto',
                                    maxHeight: 'inherit',
                                } : {}), /* NOTE: Fix unknown grid height during SSR */
                                maxWidth: '100%', // Override Grid container max-width
                            }}
                            deferredMeasurementCache={deferredMeasurementCache}
                        />
                    )}
                </InfiniteLoader>
            )}
        </WindowScroller>
    );
};
